import { useState } from "react";
import { Phone, ClipboardCheck, Wrench, ShieldCheck, ArrowUpRight } from "lucide-react";
const NAVY = "#081438";
const NAVY_LIGHT = "#243752";
const GOLD = "#F5A623";
const GOLD_DARK = "#9A6400";
const CREAM = "#FAF7F1";
const CREAM_DEEP = "#F2EDE2";
const PAPER = "#FFFEFB";
const INK = "#1A1F2E";
const INK_MUTED = "#5A6478";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";

// === Replace with the actual step image paths from your Media Library ===
const STEP_IMAGES = {
  call: "/wp-content/uploads/2026/06/step-01-call.webp",
  diagnose: "/wp-content/uploads/2026/06/step-02-diagnose.webp",
  repair: "/wp-content/uploads/2026/06/step-03-repair.webp",
  test: "/wp-content/uploads/2026/06/step-04-test.webp"
};
type StepIcon = React.ComponentType<{
  className?: string;
  strokeWidth?: number;
}>;
type Step = {
  num: string;
  tag: string;
  title: string;
  description: string;
  meta: string;
  image: string;
  icon: StepIcon;
};
const STEPS: Step[] = [{
  num: "01",
  tag: "The Call",
  title: "Tell us what's wrong",
  description: "Call or WhatsApp with your suburb and symptoms. We'll quote a live ETA and dispatch the same day.",
  meta: "Live ETA · 60 seconds",
  image: STEP_IMAGES.call,
  icon: Phone
}, {
  num: "02",
  tag: "On-Site Diagnosis",
  title: "Inspection & fixed quote",
  description: "10-minute visual and functional test of the door, motor, springs, and tracks. Fixed-price quote in writing before any work.",
  meta: "Quote in writing",
  image: STEP_IMAGES.diagnose,
  icon: ClipboardCheck
}, {
  num: "03",
  tag: "Repair or Install",
  title: "Fixed the same visit",
  description: "Fully stocked vans carry the parts for 95% of common repairs. Most jobs completed on the first visit, not a return trip.",
  meta: "95% first-visit fix",
  image: STEP_IMAGES.repair,
  icon: Wrench
}, {
  num: "04",
  tag: "Test & Warranty",
  title: "Cycled, tested, warranted",
  description: "Door cycled 5–10 times to confirm. 12-month workmanship warranty handed over in writing. Old parts shown, then disposed.",
  meta: "12-month warranty",
  image: STEP_IMAGES.test,
  icon: ShieldCheck
}];
export default function OurProcess() {
  return <section className="relative overflow-hidden" style={{
    backgroundColor: CREAM,
    fontFamily: FONT_BODY
  }} aria-labelledby="our-process-heading">
      {/* Paper grain */}
      <div aria-hidden className="absolute inset-0 opacity-[0.05] pointer-events-none mix-blend-multiply" style={{
      backgroundImage: "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.05 0 0 0 0 0.08 0 0 0 0 0.22 0 0 0 0.6 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")"
    }} />

      {/* Decorative gold blob */}
      <div aria-hidden className="absolute -top-32 right-0 w-[400px] h-[400px] rounded-full pointer-events-none opacity-[0.05]" style={{
      backgroundColor: GOLD,
      filter: "blur(80px)"
    }} />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10 pt-24 md:pt-32 pb-20 md:pb-28">

        {/* === Top strip === */}
        <div className="flex items-center justify-between gap-6 mb-12 md:mb-14 flex-wrap">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full border" style={{
            borderColor: `${GOLD_DARK}66`,
            backgroundColor: `${GOLD}1A`
          }}>
              <Wrench className="h-3.5 w-3.5" strokeWidth={2.5} style={{
              color: GOLD_DARK
            } as React.CSSProperties} />
            </span>
            <span className="text-[11px] font-bold tracking-[0.28em] uppercase" style={{
            color: GOLD_DARK
          }}>
              Our Process
            </span>
            <span className="hidden md:block h-px w-12" style={{
            backgroundColor: `${NAVY}26`
          }} />
          </div>
          <div className="text-[10px] font-semibold tracking-[0.22em] uppercase" style={{
          color: INK_MUTED
        }}>
            4 Steps · Average 3 Hours · Same Day
          </div>
        </div>

        {/* === Headline === */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-14 md:mb-20 items-end">
          <h2 id="our-process-heading" className="lg:col-span-8 font-extrabold" style={{
          fontFamily: FONT_HEAD,
          color: NAVY,
          fontSize: "clamp(2rem, 5vw, 4rem)",
          lineHeight: 1.02,
          letterSpacing: "-0.03em"
        }}>
            From{" "}
            <span style={{
            color: GOLD_DARK
          }}>your call</span>{" "}
            to a working door
            <br className="hidden md:block" />
            &mdash; in four steps.
          </h2>
          <p className="lg:col-span-4 text-[15px] leading-relaxed" style={{
          color: INK_MUTED
        }}>
            Every repair and install follows the same transparent process &mdash; fast,
            written, and warranted.
          </p>
        </div>

        {/* === 4 PHOTO CARDS === */}
        <div className="relative">
          {/* Gold thread connector (desktop only) */}
          <div aria-hidden className="absolute top-[140px] left-[5%] right-[5%] h-px hidden lg:block pointer-events-none" style={{
          background: `repeating-linear-gradient(90deg, ${GOLD}66 0, ${GOLD}66 8px, transparent 8px, transparent 16px)`
        }} />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {STEPS.map(step => <StepCard key={step.num} step={step} />)}
          </div>
        </div>

        {/* === Bottom CTA strip === */}
        <div className="mt-16 md:mt-20">
          <div className="relative rounded-2xl border overflow-hidden p-6 md:p-8 flex items-center justify-between gap-6 flex-wrap" style={{
          backgroundColor: NAVY,
          borderColor: `${GOLD}33`
        }}>
            {/* Top gold accent */}
            <div aria-hidden className="absolute top-0 left-8 right-8 h-[2px]" style={{
            background: `linear-gradient(90deg, ${GOLD} 0%, ${GOLD}80 50%, transparent 100%)`
          }} />

            <div className="flex-1 min-w-[260px]">
              <div className="text-[10px] font-bold tracking-[0.28em] uppercase mb-1.5" style={{
              color: GOLD
            }}>
                Start at step 01
              </div>
              <h3 className="font-bold text-white" style={{
              fontFamily: FONT_HEAD,
              fontSize: "clamp(1.125rem, 2vw, 1.5rem)",
              lineHeight: 1.2,
              letterSpacing: "-0.015em"
            }}>
                Tell us what your door is doing &mdash; we&rsquo;ll handle the rest.
              </h3>
            </div>

            <a href="tel:0485000933" className="group inline-flex items-center gap-2.5 rounded-full px-6 py-3 text-[14px] font-bold transition-all duration-200 hover:scale-[1.03] active:scale-[0.98]" style={{
            backgroundColor: GOLD,
            color: NAVY,
            fontFamily: FONT_BODY,
            boxShadow: `0 14px 40px -10px ${GOLD}99`
          }}>
              <Phone className="h-4 w-4" strokeWidth={2.5} />
              0485 000 933
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.5} />
            </a>
          </div>
        </div>
      </div>
    </section>;
}
function StepCard({
  step
}: {
  step: Step;
}) {
  const [imgError, setImgError] = useState(false);
  const Icon = step.icon;
  return <div className="group relative rounded-2xl overflow-hidden border bg-white transition-all duration-400" style={{
    borderColor: `${NAVY}14`,
    boxShadow: `0 8px 24px -12px ${NAVY}1A`
  }} onMouseEnter={e => {
    e.currentTarget.style.borderColor = `${GOLD_DARK}66`;
    e.currentTarget.style.transform = "translateY(-4px)";
    e.currentTarget.style.boxShadow = `0 28px 60px -20px ${NAVY}33, 0 0 0 1px ${GOLD}26`;
  }} onMouseLeave={e => {
    e.currentTarget.style.borderColor = `${NAVY}14`;
    e.currentTarget.style.transform = "translateY(0)";
    e.currentTarget.style.boxShadow = `0 8px 24px -12px ${NAVY}1A`;
  }}>
      {/* Photo top half */}
      <div className="relative overflow-hidden" style={{
      aspectRatio: "4 / 3",
      backgroundColor: CREAM_DEEP
    }}>
        {!imgError ? <img src={step.image} alt={`Step ${step.num}: ${step.title}`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" decoding="async" onError={() => setImgError(true)} /> :
      // Fallback placeholder when image fails to load
      <div className="w-full h-full flex items-center justify-center" style={{
        background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)`
      }}>
            <Icon className="h-16 w-16 opacity-50" strokeWidth={1.5} style={{
          color: GOLD
        } as React.CSSProperties} />
          </div>}

        {/* Dark gradient at bottom for legibility */}
        <div aria-hidden className="absolute inset-0 pointer-events-none" style={{
        background: `linear-gradient(180deg, transparent 0%, transparent 50%, ${NAVY}CC 100%)`
      }} />

        {/* Step number overlay top-left */}
        <div className="absolute top-4 left-4 font-extrabold leading-none select-none" style={{
        fontFamily: FONT_HEAD,
        color: "white",
        fontSize: "2.25rem",
        letterSpacing: "-0.04em",
        textShadow: `0 4px 20px ${NAVY}99`,
        fontVariantNumeric: "tabular-nums"
      }}>
          <span style={{
          color: GOLD
        }}>{step.num}</span>
        </div>

        {/* Icon badge top-right */}
        <div className="absolute top-4 right-4 flex items-center justify-center w-9 h-9 rounded-full backdrop-blur-md border" style={{
        backgroundColor: "rgba(255,255,255,0.20)",
        borderColor: "rgba(255,255,255,0.30)"
      }}>
          <Icon className="h-[18px] w-[18px]" strokeWidth={2} style={{
          color: "white"
        } as React.CSSProperties} />
        </div>

        {/* Tag at bottom-left of photo */}
        <div className="absolute bottom-3 left-4 right-4">
          <div className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{
          color: GOLD
        }}>
            {step.tag}
          </div>
        </div>
      </div>

      {/* Content bottom half */}
      <div className="p-5 md:p-6 flex flex-col" style={{
      minHeight: "180px"
    }}>
        <h3 className="font-bold mb-2 transition-colors duration-200 group-hover:text-[#9A6400]" style={{
        fontFamily: FONT_HEAD,
        color: NAVY,
        fontSize: "1.0625rem",
        lineHeight: 1.25,
        letterSpacing: "-0.012em"
      }}>
          {step.title}
        </h3>

        <p className="text-[13.5px] leading-[1.6] mb-4 flex-grow" style={{
        color: INK_MUTED
      }}>
          {step.description}
        </p>

        {/* Meta divider */}
        <div aria-hidden className="h-px mb-3" style={{
        background: `linear-gradient(90deg, ${GOLD}40 0%, transparent 100%)`
      }} />

        {/* Meta line */}
        <div className="flex items-center gap-1.5">
          <span className="inline-block w-1 h-1 rounded-full" style={{
          backgroundColor: GOLD
        }} />
          <span className="text-[10px] font-bold tracking-[0.14em] uppercase" style={{
          color: GOLD_DARK
        }}>
            {step.meta}
          </span>
        </div>
      </div>
    </div>;
}